import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Heart } from 'lucide-react'
import { t } from '../../i18n/translations'
import { logger } from '../../utils/logger'
import ProductCard from '../product/ProductCard'
import Footer from '../layout/Footer'
import { productApi, Product } from '../../services/api'

interface FavoriteProduct extends Product {
  favorited_at?: string
}

export default function Favorites() {
  const navigate = useNavigate()

  const [favorites, setFavorites] = useState<FavoriteProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await productApi.getFavorites()
        setFavorites(data)
      } catch (err) {
        logger.error('Failed to fetch favorites', err)
        setError(t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchFavorites()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">{t('common.back')}</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            {t('profile.myFavorites')}
          </h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="p-4">
        {error ? (
          <div className="text-center text-gray-500 py-8">{error}</div>
        ) : favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('common.noResults')}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 text-blue-500 hover:text-blue-600"
            >
              {t('nav.browse')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {favorites.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>
        )}
        <Footer />
      </div>
    </div>
  )
}
